/*
 * Shopify API for fetching shop data, creating checkouts and managing user accounts.
 */

import type { Product } from '$lib/types/ecom/apiTypes';
import { error } from '@sveltejs/kit';
import { GraphQLClient } from 'graphql-request';
import { gql } from 'graphql-request';

import { customerToken, customer, type CartItem } from '$lib/stores/shopStore';
import { setCookie } from '$lib/util/helpers';
import { init as initCart } from './shopifyCart';

let graphQLClient: GraphQLClient;

export default {
  init(api_key: string, endpoint: string) {
    
    graphQLClient = new GraphQLClient(endpoint, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': api_key
      }
    });
    initCart(graphQLClient);
  },

  /**
   * Login in user given the email and password
   * @param email      The email of the user account
   * @param password   The password of the user account
   * @param $store     Reference to the VueX-Store (for saving login token)
   * @param $cookies   Reference to the "cookie-universal-nuxt" plugin (for saving login token)
   */
  async login(email, password) {
    const mutation = gql`
			mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
				customerAccessTokenCreate(input: $input) {
					customerAccessToken {
						accessToken
						expiresAt
					}
					customerUserErrors {
						code
						field
						message
					}
				}
			}
		`;

    const variables = {
      input: {
        email,
        password
      }
    };

    let data;
    try {
      data = await graphQLClient.request(mutation, variables);
    } catch (err) {
      return { success: false, error: `error_internal ${err}` };
    }

    if (data.customerAccessTokenCreate.customerUserErrors.length === 0) {
      customerToken.set(data.customerAccessTokenCreate.customerAccessToken.accessToken);

      const query =
        gql`
          query customer {
            customer(customerAccessToken: "` +
        data.customerAccessTokenCreate.customerAccessToken.accessToken +
        `"){
              id,
              displayName,
              firstName,
              lastName,
              email,
              addresses(first: 10) {
                edges {
                  node {
                    address1,
                    address2,
                    city,
                    zip,
                    country,
                    id
                  }
                }
              }
            }
          }
        `;

      let customerObj;
      try {
        customerObj = await graphQLClient.request(query);
      } catch (err) {
        return { success: false, error: `error_internal_1 ${err}` };
      }

      customer.set(customerObj);

      setCookie(
        'customer-token',
        data.customerAccessTokenCreate.customerAccessToken.accessToken,
        data.customerAccessTokenCreate.customerAccessToken.expiresAt
      );

      return {
        success: true,
        token: data.customerAccessTokenCreate.customerAccessToken
      };
    } else {
      // if (data.customerAccessTokenCreate.customerUserErrors[0].code === 'UNIDENTIFIED_CUSTOMER') {}
      return { success: false, error: 'error_wrong_credentials' };
    }
  },

  /**
   * Signup user
   * @param inputData    Object with fields {'email', 'password', 'firstName', 'lastName', 'phone'}
   */
  async signup(inputData, lang = 'en') {
    const mutation = gql`
      mutation customerCreate($input: CustomerCreateInput!) @inContext(language: ${lang.toUpperCase()})
      {
        customerCreate(input: $input) {
          customer {
            id
          }
          customerUserErrors {
            code
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: inputData
    };

    try {
      const data = await graphQLClient.request(mutation, variables);
      return data;
    } catch (err) {
      return null;
    }
  },

  /**
   * Activte user
   * @param password        The users password
   * @param activation_url  The shopify activation url from the email
   * @param lang            The language
   */
  async activateUser(password: string, activationUrl: string, lang = 'en') {
    const mutation = gql`
    mutation customerActivateByUrl($activationUrl: URL!, $password: String!) @inContext(language: ${lang.toUpperCase()}) {
      customerActivateByUrl(activationUrl: $activationUrl, password: $password) {
        customer {
          id,
          displayName,
          firstName,
          lastName,
          email,
          addresses(first: 10) {
            edges {
              node {
                address1,
                address2,
                city,
                zip,
                country,
                id
              }
            }
          }
        }
        customerAccessToken {
          accessToken
          expiresAt
        }
        customerUserErrors {
          code
          field
          message
        }
      }
    }
    `;

    const variables = {
      password,
      activationUrl
    };

    try {
      const data = await graphQLClient.request(mutation, variables);
      return data;
    } catch (err) {
      return err;
    }
  },

  /*
   * Get customer data with an access token
   * @param token    The customer access token
   * @param error    Reference to the nuxt error object
   */
  async getCustomer(token) {
    if (!token) {
      return undefined
    }

    const query =
      gql`
       query customer {
         customer(customerAccessToken: "` +
      token +
      `"){
           id,
           displayName,
           firstName,
           lastName,
           email,
           type: metafield(namespace: "custom", key: "type") {
             value
           }
           addresses(first: 10) {
             edges {
               node {
                 firstName,
                 lastName,
                 company,
                 phone,
                 address1,
                 address2,
                 city,
                 zip,
                 country,
                 id
               }
             }
           }
         }
       }
     `;

    try {
      const customer = await graphQLClient.request(query);
      return customer;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Get product tags
   * @param error    Reference to the nuxt error object
   */
  async getTags() {
    const query = gql`
			query tags {
				productTags(first: 100) {
					edges {
						node
					}
				}
			}
		`;

    let tags;
    try {
      tags = await graphQLClient.request(query);
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }

    const names = [];

    for (const tag of tags?.productTags?.edges ?? []) {
      names.push(tag.node);
    }
    return names;
  },

  /*
   * Get full list of available countries in shopify
   * @param error    Reference to the nuxt error object
   */
  async getCountries() {
    const query = gql`
			query countries {
				__type(name: "CountryCode") {
					countries: enumValues {
						name
						description
					}
				}
			}
		`;

    let data;
    try {
      data = await graphQLClient.request(query);
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }

    const countries = [];

    for (const country of data.__type.countries) {
      countries.push(country.description);
    }
    return countries;
  },

  /**
   */
  async getLocations(lang = 'en') {
    const query = gql`
      query AllLocalizations @inContext(language: ${lang.toUpperCase()}) {
        localization {
          availableCountries {
            isoCode
            name
            currency {
              isoCode
              name
              symbol
            }
            market {
              handle
            }
          }
        }
      }
    `;

    let data: any;
    try {
      data = await graphQLClient.request(query);
    } catch (err) {
      console.log(err);
      error(500, `Internal error: ${err}`);
    }

    const locations = [];

    for (const location of data?.localization?.availableCountries ?? []) {
      locations.push(location);
    }
    return locations;
  },

  /*
   * Get shop collections
   * @param lang
   * @param error    Reference to the nuxt error object
   */
  async getCollections(lang = 'en') {
    const query = gql`
      query collections @inContext(language: ${lang.toUpperCase()}){
        collections(first: 100) {
          edges {
            node {
              id
              title
              handle
              special: metafield(namespace: "custom", key: "special") {
                value
              }
              hidden: metafield(namespace: "custom", key: "hidden") {
                value
              }
              sort: metafield(namespace: "custom", key: "sort") {
                value
              }
              show_filters: metafield(namespace: "custom", key: "show_filters") {
                value
              }
              image_inactive: metafield(namespace: "custom", key: "image_inactive") {
                reference {
                  ... on MediaImage {
                    mediaContentType
                    image {
                      url (transform: {
                        maxHeight: 32,
                        maxWidth: 32, 
                        preferredContentType: WEBP
                      })
                    }
                  }
                }
              }
              image_active: metafield(namespace: "custom", key: "image_active") {
                reference {
                  ... on MediaImage {
                    mediaContentType
                    image {
                      url (transform: {
                        maxHeight: 32,
                        maxWidth: 32, 
                        preferredContentType: WEBP
                      })
                    }
                  }
                }
              }
              image {
                id
                altText
                url (transform: {
                  maxHeight: 1024,
                  maxWidth: 1024, 
                  preferredContentType: WEBP
                })
              }
            }
          }
        }
      }`;

    try {
      const collections = await graphQLClient.request(query);
      return collections;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  async getCollection(id: string, lang: string = 'en') {
    const query = gql`
      query getCollectionById($id: ID!) @inContext(language: ${lang.toUpperCase()}){
        collection(id: $id) {
          handle
        }
      }`;

    const variables = {
      id
    };


    try {
      const data = await graphQLClient.request(query, variables);
      return data;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Get product data
   * @param collection    Optional collection handle (null for all products)
   * @param cursor        Offset-Cursor for pagination, provide if you want to fetch the next x products
   *                      after the product with the cursor-id
   * @param sortKey       How to sort products (https://shopify.dev/api/storefront/2022-01/enums/ProductCollectionSortKeys)
   * @param reverse       Reverse product order?
   * @param error         Reference to the nuxt error object
   */
  async getCollectionProducts(
    collection: string | undefined,
    cursor?: string | undefined,
    first?: number | undefined,
    sortKey?: string | undefined,
    reverse?: boolean | undefined,
    country?: string,
    lang = 'en'
  ): Promise<any> {
    const variables = {
      after: cursor,
      collection,
      first,
      sortKey,
      reverse
    };

    const query = gql`
      query products (
        $after: String
        $collection: String
        $first: Int
        $sortKey: ProductCollectionSortKeys
        $reverse: Boolean
      ) @inContext(language: ${lang.toUpperCase()}, country: ${country}){
        collection(handle: $collection) {
          title
          products(
            first: $first
            after: $after
            sortKey: $sortKey
            reverse: $reverse
          ) {
            edges {
              cursor
              node {
                id
                title
                tags
                handle
                availableForSale
                collections(first: 4) {
                  edges {
                    node {
                      id,
                      title,
                      handle
                    }
                  }
                },
                product_type: metafield(namespace: "custom", key: "product_type") {
                  value
                }
                subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
                value
              }
                country: metafield(namespace: "my_fields", key: "country") {
                  value
                }
                tag: metafield(namespace: "custom", key: "tag") {
                  value
                }
                card_text: metafield(namespace: "custom", key: "card_text") {
                  value
                }
                tasting_notes: metafield(namespace: "custom", key: "tasting_notes") {
                  references(first: 20) {
                    edges {
                      node {
                        ... on Metaobject {
                          handle
                        }
                      }
                    }
                  }
                }
                roast_level: metafield(namespace: "custom", key: "roast_level") {
                  value
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      availableForSale
                      selectedOptions {
                        name
                        value
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                      compareAtPriceV2 {
                        amount
                        currencyCode
                      }
                      image {
                        id
                        altText
                        url (transform: {
                          maxHeight: 1024,
                          maxWidth: 1024, 
                          preferredContentType: WEBP
                        })
                      }
                    }
                  }
                }
                media(first: 1) {
                  edges {
                    node {
                      previewImage {
                        id
                        altText
                        url (transform: {
                          maxHeight: 1024,
                          maxWidth: 1024, 
                          preferredContentType: WEBP
                        })
                      }
                    }
                  }
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      }`;

    try {
      const data = await graphQLClient.request(query, variables);

      return data;
    } catch (err) {
      console.error(err);

      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Get product data
   * @param cursor        Offset-Cursor for pagination, provide if you want to fetch the next x products
   *                      after the product with the cursor-id
   * @param sortKey       How to sort products (https://shopify.dev/api/storefront/2022-01/enums/ProductSortKeys)
   * @param reverse       Reverse product order?
   * @param error         Reference to the nuxt error object
   * @param search        Optional search text
   */
  async getProducts(
    search: string,
    cursor?: string | undefined,
    first: number,
    sortKey?: string | undefined,
    reverse: boolean = false,
    country: string,
    lang = 'en'
  ): Promise<any> {
    let productQuery = null;

    if (search) {
      productQuery = search;
    }

    const variables = {
      after: cursor,
      query: productQuery,
      first,
      sortKey,
      reverse
    };

    const query = gql`
      query products(
        $after: String
        $query: String
        $first: Int
        $sortKey: ProductSortKeys
        $reverse: Boolean
      ) @inContext(language: ${lang.toUpperCase()}, country: ${country}) {
        products(
          first: $first
          after: $after
          sortKey: $sortKey
          reverse: $reverse
          query: $query
        ) {
          edges {
            cursor
            node {
              id
              title
              tags
              description
              handle
              availableForSale
              subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
                value
              }
              product_type: metafield(namespace: "custom", key: "product_type") {
                value
              }
              subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
                value
              }
              country: metafield(namespace: "my_fields", key: "country") {
                value
              }
              tag: metafield(namespace: "custom", key: "tag") {
                value
              }
              card_text: metafield(namespace: "custom", key: "card_text") {
                value
              }
              card_overlay_image: metafield(namespace: "custom", key: "card_overlay_image") {
                reference {
                  ... on MediaImage {
                    mediaContentType
                    image {
                      url (transform: {
                        maxHeight: 800,
                        maxWidth: 800, 
                        preferredContentType: WEBP
                      })
                    }
                  }
                  ... on Video {
                    id
                    mediaContentType
                    sources {
                      mimeType
                      url
                      width
                    }
                  }
                }
              }
              tasting_notes: metafield(namespace: "custom", key: "tasting_notes") {
                references(first: 20) {
                  edges {
                    node {
                      ... on Metaobject {
                        handle
                      }
                    }
                  }
                }
              }
              roast_level: metafield(namespace: "custom", key: "roast_level") {
                value
              }
              producerHandle: metafield(
                namespace: "my_fields"
                key: "producer"
              ) {
                value
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    availableForSale
                    selectedOptions {
                      name
                      value
                    }
                    priceV2 {
                      amount
                      currencyCode
                    }
                    compareAtPriceV2 {
                      amount
                      currencyCode
                    }
                    image {
                      id
                      altText
                      url (transform: {
                        maxHeight: 800,
                        maxWidth: 800, 
                        preferredContentType: WEBP
                      })
                    }
                  }
                }
              }
              media(first: 1) {
                edges {
                  node {
                    previewImage {
                      id
                      altText
                      url (transform: {
                        maxHeight: 800,
                        maxWidth: 800, 
                        preferredContentType: WEBP
                      })
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }`;

    try {
      const data = await graphQLClient.request(query, variables);
      return {
        collection: {
          title: 'All',
          products: data.products
        }
      };
    } catch (err) {
      console.error('err', err);
      error(500, `Internal error: ${err}`);
    }
  },

  /**
   * Searches for a given string, works almost like getProducts but returns a slightly different data structure due to
   * differences in the shopify endpoint.
   * 
   * @param search The search term
   * @param cursor The starting cursor/position from which to return products (used for pagination)
   * @param first The number of results to return
   * @param sortKey How the products are sorted
   * @param reverse Should the order be reversed?
   * @param country The currently active country
   * @param lang The currently acive language
   * @returns Search results
   */
  async searchProducts(
    search: string,
    cursor?: string | undefined,
    first: number,
    sortKey?: string | undefined,
    reverse: boolean = false,
    country: string,
    lang = 'en'
  ): Promise<any> {
    let productQuery = null;

    if (search) {
      productQuery = search;
    }

    const variables = {
      after: cursor,
      query: productQuery,
      first,
      sortKey,
      reverse
    };

    const query = gql`
      query search(
        $after: String
        $query: String!
        $first: Int
        $sortKey: SearchSortKeys
        $reverse: Boolean
      ) @inContext(language: ${lang.toUpperCase()}, country: ${country}) {
        search(
          first: $first
          after: $after
          sortKey: $sortKey
          reverse: $reverse
          query: $query
          types: PRODUCT
        ) {
          edges {
            node {
              ... on Product {
                id
                title
                tags
                description
                handle
                availableForSale
                subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
                  value
                }
                product_type: metafield(namespace: "custom", key: "product_type") {
                  value
                }
                subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
                  value
                }
                country: metafield(namespace: "my_fields", key: "country") {
                  value
                }
                tag: metafield(namespace: "custom", key: "tag") {
                  value
                }
                card_text: metafield(namespace: "custom", key: "card_text") {
                  value
                }
                card_overlay_image: metafield(namespace: "custom", key: "card_overlay_image") {
                  reference {
                    ... on MediaImage {
                      mediaContentType
                      image {
                        url (transform: {
                          maxHeight: 800,
                          maxWidth: 800, 
                          preferredContentType: WEBP
                        })
                      }
                    }
                    ... on Video {
                      id
                      mediaContentType
                      sources {
                        mimeType
                        url
                        width
                      }
                    }
                  }
                }
                tasting_notes: metafield(namespace: "custom", key: "tasting_notes") {
                  references(first: 20) {
                    edges {
                      node {
                        ... on Metaobject {
                          handle
                        }
                      }
                    }
                  }
                }
                roast_level: metafield(namespace: "custom", key: "roast_level") {
                  value
                }
                producerHandle: metafield(
                  namespace: "my_fields"
                  key: "producer"
                ) {
                  value
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      availableForSale
                      selectedOptions {
                        name
                        value
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                      compareAtPriceV2 {
                        amount
                        currencyCode
                      }
                      image {
                        id
                        altText
                        url (transform: {
                          maxHeight: 800,
                          maxWidth: 800, 
                          preferredContentType: WEBP
                        })
                      }
                    }
                  }
                }
                media(first: 1) {
                  edges {
                    node {
                      previewImage {
                        id
                        altText
                        url (transform: {
                          maxHeight: 800,
                          maxWidth: 800, 
                          preferredContentType: WEBP
                        })
                      }
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }`;

    try {
      const data = await graphQLClient.request(query, variables);
      return {
        collection: {
          title: 'All',
          products: data?.search ?? [],
          pageInfo: data?.search?.pageInfo
        }
      };
    } catch (err) {
      console.error('err', err);
      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Get the most expensive product
   *
   * @param error         Reference to the nuxt error object
   */
  async getMostExpensiveProducts(country: string) {
    const variables = {};

    const query = gql`
      query products @inContext(country: ${country})  {
        products(first: 1, sortKey: PRICE, reverse: true) {
          edges {
            node {
              id
              title
              tags
              description
              handle
              availableForSale
              subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
                value
              }
              product_type: metafield(namespace: "custom", key: "product_type") {
                value
              }
              country: metafield(namespace: "my_fields", key: "country") {
                value
              }
              producerHandle: metafield(
                namespace: "my_fields"
                key: "producer"
              ) {
                value
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    availableForSale
                    selectedOptions {
                      name
                      value
                    }
                    priceV2 {
                      amount,
                      currencyCode
                    }
                    image {
                      id
                      altText
                      url (transform: {
                        maxHeight: 1024,
                        maxWidth: 1024, 
                        preferredContentType: WEBP
                      })
                    }
                  }
                }
              }
              media(first: 1) {
                edges {
                  node {
                    previewImage {
                      id
                      altText
                      url (transform: {
                        maxHeight: 1024,
                        maxWidth: 1024, 
                        preferredContentType: WEBP
                      })
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            hasNextPage
          }
        }
      }
    `;

    try {
      const data = await graphQLClient.request(query, variables);
      return data;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Get product data
   * @param handle   The product handle
   * @param error    Reference to the nuxt error object
   */
  async getProductByHandle(handle: string, country: string, lang: string = 'en') {
    const query = gql`
      query product @inContext(language: ${lang.toUpperCase()}, country: ${country}){
        product(handle: "${handle}"){
          id,
          title,
          tags,
          descriptionHtml,
          description,
          handle,
          availableForSale,
          options {
            name,
            values
          },
          seo {
            title
            description
          }
          subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
            value
          }
          subtitle_2: metafield(namespace: "custom", key: "subtitle_2") {
            value
          }
          product_type: metafield(namespace: "custom", key: "product_type") {
            value
          }
          roast_level: metafield(namespace: "custom", key: "roast_level") {
            value
          }
          caffeine_level: metafield(namespace: "custom", key: "caffeine_level") {
            value
          }
          acidity_level: metafield(namespace: "custom", key: "acidity_level") {
            value
          }
          variety: metafield(namespace: "custom", key: "variety") {
            value
          }
          country: metafield(namespace: "custom", key: "country") {
            value
          }
          processing: metafield(namespace: "custom", key: "processing") {
            value
          }
          processing: metafield(namespace: "custom", key: "processing") {
            value
          }
          tasting_notes: metafield(namespace: "custom", key: "tasting_notes") {
            references(first: 20) {
              edges {
                node {
                  ... on Metaobject {
                    handle
                  }
                }
              }
            }
          }
          machine_types: metafield(namespace: "custom", key: "machine_types") {
            references(first: 20) {
              edges {
                node {
                  ... on Metaobject {
                    handle
                  }
                }
              }
            }
          }

          sellingPlanGroups(first: 2) {
            edges {
              node {
                sellingPlans(first: 8) {
                  edges {
                    node {
                      name
                      id
                      deliveryPolicy {
                        ... on SellingPlanRecurringDeliveryPolicy {
                          intervalCount
                          interval
                        }
                      }
                      priceAdjustments {
                        adjustmentValue {
                          ... on SellingPlanPercentagePriceAdjustment {
                            adjustmentPercentage
                          } 
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          
          priceRange {
            minVariantPrice {
              amount,
              currencyCode
            },
            maxVariantPrice {
              amount,
              currencyCode
            },
          },
          
          variants(first: 10){
            edges {
              node {
                id,
                priceV2 {
                  amount,
                  currencyCode
                },
                compareAtPriceV2 {
                  amount,
                  currencyCode
                },
                unitPrice {
                  amount,
                  currencyCode 
                },
                unitPriceMeasurement {
                  referenceValue,
                  quantityUnit
                },
                availableForSale,
                quantityAvailable,
                weight,
                weightUnit,
                selectedOptions {
                  name,
                  value
                },
                image {
                  id,
                  altText,
                  url (transform: {
                    maxHeight: 768,
                    maxWidth: 768, 
                    preferredContentType: WEBP
                  })
                }
              }
            }
          },
          collections(first: 4) {
            edges {
              node {
                id,
                title,
                handle
                show_filters: metafield(namespace: "custom", key: "show_filters") {
                  value
                }
                show_alternative_benefits: metafield(namespace: "custom", key: "show_alternative_benefits") {
                  value
                }
              }
            }
          },
          media(first: 10) {
            edges {
              node {
                mediaContentType
                previewImage {
                  id,
                  altText,
                  originalSrc,
                  url (transform: {
                    maxHeight: 768,
                    maxWidth: 768, 
                    preferredContentType: WEBP
                  })
                }
                ... on Video {
                  id
                  mediaContentType
                  sources {
                    mimeType
                    url
                    width
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data: any = await graphQLClient.request(query);
      return data?.product as Product;
    } catch (err) {
      console.error(err);

      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Get multiple products by id
   * @param ids      An array of shopify product id
   */
  async getProductsById(ids: string[], country: string, lang = 'en'): Promise<any> {
    if (!ids || ids.length === 0) {
      return {
        nodes: []
      };
    }

    // const gids = ids.map(id => Buffer.from(`gid://shopify/Product/${id}`).toString('base64'))
    const query = gql`
    query products @inContext(language: ${lang.toUpperCase()}, country: ${country}) {
      nodes(ids: ${JSON.stringify(ids)}) {
        ...on Product {
          id,
          title,
          handle,
          description,
          tags,
          availableForSale,
          collections(first: 4) {
            edges {
              node {
                handle
              }
            }
          }
          subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
            value
          }
          product_type: metafield(namespace: "custom", key: "product_type") {
            value
          }
          card_text: metafield(namespace: "custom", key: "card_text") {
            value
          }
          card_overlay_image: metafield(namespace: "custom", key: "card_overlay_image") {
            reference {
              ... on MediaImage {
                mediaContentType
                image {
                  url (transform: {
                    maxHeight: 800,
                    maxWidth: 800, 
                    preferredContentType: WEBP
                  })
                }
              }
              ... on Video {
                id
                mediaContentType
                sources {
                  mimeType
                  url
                  width
                }
              }
            }
          }
          variants(first: 10){
            edges {
              node {
                id,
                priceV2 { amount, currencyCode },
                compareAtPriceV2 {
                  amount
                  currencyCode
                },
                unitPrice { amount, currencyCode },
                unitPriceMeasurement {
                  referenceValue,
                  quantityUnit
                },
                availableForSale,
                quantityAvailable,
                selectedOptions {
                  name,
                  value
                },
                image {
                  id,
                  altText,
                  url (transform: {
                    maxHeight: 1024,
                    maxWidth: 1024, 
                    preferredContentType: WEBP
                  })
                }
              }
            }
          },
          media(first: 1) {
            edges {
              node {
                previewImage {
                  id,
                  altText,
                  url (transform: {
                    maxHeight: 1024,
                    maxWidth: 1024, 
                    preferredContentType: WEBP
                  })
                }
              }
            }
          }
        }
      }
    }`;

    try {
      const data = await graphQLClient.request(query);
      return data;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  async getProductsCartInfoById(ids: string[], country: string, lang = 'en'): Promise<any> {
    if (!ids || ids.length === 0) {
      return {
        nodes: []
      };
    }

    const query = gql`
    query products @inContext(language: ${lang.toUpperCase()}, country: ${country}) {
      nodes(ids: ${JSON.stringify(ids)}) {
        ...on Product {
          id,
          handle,
          title,
          subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
            value
          }
          product_type: metafield(namespace: "custom", key: "product_type") {
            value
          }
          variants(first: 10){
            edges {
              node {
                id,
                priceV2 { amount, currencyCode },
                compareAtPriceV2 {
                  amount
                  currencyCode
                },
                availableForSale,
                quantityAvailable,
                selectedOptions {
                  name,
                  value
                },
                image {
                  id,
                  altText,
                  url (transform: {
                    maxHeight: 1024,
                    maxWidth: 1024, 
                    preferredContentType: WEBP
                  })
                }
              }
            }
          },
        }
      }
    }`;

    try {
      const data = await graphQLClient.request(query);
      return data;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Get recommended products for a given product
   * @param productId  The product id for the project to fetch recommendations
   * @param error      Reference to the nuxt error object
   */
  async getProductRecommendations(productId: string, country: string, lang = 'en'): Promise<any> {
    const query =
      gql`
      query products @inContext(language: ${lang.toUpperCase()}, country: ${country}) {
        productRecommendations(productId: "` +
      productId +
      `"){
          id,
          title,
          tags,
          handle,
          availableForSale,
          subtitle_1: metafield(namespace: "custom", key: "subtitle_1") {
            value
          }
          product_type: metafield(namespace: "custom", key: "product_type") {
            value
          }
          country: metafield(namespace: "my_fields", key: "country"){
            value
          },
          priceRange {
            minVariantPrice { amount, currencyCode },
            maxVariantPrice { amount, currencyCode }
          },
          variants(first: 10){
            edges {
              node {
                id,
                priceV2 { amount, currencyCode },
                availableForSale,
                quantityAvailable,
                selectedOptions {
                  name,
                  value
                },
                image {
                  id,
                  altText,
                  url (transform: {
                    maxHeight: 1024,
                    maxWidth: 1024, 
                    preferredContentType: WEBP
                  })
                }
              }
            }
          },
          media(first: 1) {
            edges {
              node {
                previewImage {
                  id,
                  altText,
                  url (transform: {
                    maxHeight: 1024,
                    maxWidth: 1024, 
                    preferredContentType: WEBP
                  })
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data = await graphQLClient.request(query);
      return data;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Checkout action for cart
   * @param cart     Object containing products in the format [id: {amount: 1, name: '',...}]
   * @param error    Reference to the nuxt error object
   */
  async checkout(cart: CartItem[], token?: string, address?: string) {
    const products = [];
    for (const item of cart) {
      products.push({ variantId: item.id, quantity: item.amount });
    }

    // Create checkout
    const mutationCreate = gql`
			mutation checkout($input: CheckoutCreateInput!) {
				checkoutCreate(input: $input) {
					checkout {
						id
						webUrl
						lineItems(first: 5) {
							edges {
								node {
									title
									quantity
								}
							}
						}
					}
				}
			}
		`;

    const variablesCreate = {
      input: {
        lineItems: products,
        shippingAddress: address
      }
    };

    if (!address) {
      delete variablesCreate.input.shippingAddress;
    }

    const dataCreate: any = await graphQLClient.request(mutationCreate, variablesCreate);

    let dataAssociate = null;
    if (token) {
      // Associate customer with checkout
      const mutationAssociate = gql`
				mutation associateCustomerWithCheckout($checkoutId: ID!, $customerAccessToken: String!) {
					checkoutCustomerAssociateV2(
						checkoutId: $checkoutId
						customerAccessToken: $customerAccessToken
					) {
						checkout {
							id
						}
						checkoutUserErrors {
							code
							field
							message
						}
						customer {
							id
						}
					}
				}
			`;

      const variablesAssociate = {
        checkoutId: dataCreate?.checkoutCreate?.checkout?.id,
        customerAccessToken: token
      };

      dataAssociate = await graphQLClient.request(mutationAssociate, variablesAssociate);
    }

    return {
      dataCreate,
      dataAssociate
    };
  },

  /*
   * Get all orders for given customer
   * @param token    The customer access token
   * @param error    Reference to the nuxt error object
   */
  async getOrders(accessToken) {
    const query =
      gql`
      query orders {
        customer(customerAccessToken: "` +
      accessToken +
      `"){
          orders(first: 100) {
            edges {
              node {
                id,
                name,
                orderNumber,
                processedAt,
                billingAddress {
                  firstName,
                  lastName,
                  company,
                  phone,
                  name,
                  address1,
                  address2,
                  city,
                  country,
                  zip
                },
                shippingAddress {
                  firstName,
                  lastName,
                  company,
                  phone,
                  name,
                  address1,
                  address2,
                  city,
                  country,
                  zip
                },
                financialStatus,
                fulfillmentStatus,
                phone,
                currentTotalTax {
                  amount
                  currencyCode
                },
                subtotalPriceV2 {
                  amount
                  currencyCode
                },
                totalPriceV2 {
                  amount
                  currencyCode
                },
                totalShippingPriceV2 {
                  amount
                  currencyCode
                },
                totalTaxV2 {
                  amount
                  currencyCode
                },
                lineItems(first: 100) {
                  edges {
                    node {
                      title,
                      quantity,
                      discountedTotalPrice {
                        amount
                        currencyCode
                      }
                      variant {
                        image {
                          url
                        },
                        product {
                          handle
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const data = await graphQLClient.request(query);
      return data;
    } catch (err) {
      error(500, `Internal error: ${err}`);
    }
  },

  /*
   * Upate user data
   * @param token        The customer access token
   * @param error        Reference to the nuxt error object
   */
  async updateUser(token, firstName, lastName, email) {
    const mutation = gql`
			mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
				customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
					customer {
						id
					}
					customerAccessToken {
						accessToken
						expiresAt
					}
					customerUserErrors {
						code
						field
						message
					}
				}
			}
		`;

    const variables = {
      customerAccessToken: token,
      customer: {
        firstName,
        lastName,
        email
      }
    };

    const data = await graphQLClient.request(mutation, variables);
    return data;
  },

  /*
   * Recover User (Forget password functionality first step, sends the recovery mail)
   * @email   Email of the user
   */
  async recoverUser(email) {
    const mutation = gql`
			mutation customerRecover($email: String!) {
				customerRecover(email: $email) {
					customerUserErrors {
						code
						field
						message
					}
				}
			}
		`;

    const variables = {
      email
    };

    let data;
    try {
      data = await graphQLClient.request(mutation, variables);
      return data;
    } catch (err) {
      // Not idea what the fuck is happening here, shopify sends
      // valid json as response if rate limit is exceeded but
      // for some reason the client generates an error that conatins
      // the error as string and the jsons response stringified in it
      return {
        error:
          'Limit für das Zurücksetzen des Kennwort überschritten. Bitte versuchen Sie es später noch einmal.'
      };
    }
  },

  /*
   * Adds a new address to the address-list of the customer
   * @param address    Object with fields {"address1", "city" "zip", "country"}
   * @param token      The customer access token
   */
  async addAddress(address, token) {
    const mutation = gql`
			mutation customerAddressCreate(
				$customerAccessToken: String!
				$address: MailingAddressInput!
			) {
				customerAddressCreate(customerAccessToken: $customerAccessToken, address: $address) {
					customerAddress {
						id
					}
					customerUserErrors {
						code
						field
						message
					}
				}
			}
		`;

    const variables = {
      address,
      customerAccessToken: token
    };

    const data = await graphQLClient.request(mutation, variables);
    return data;
  },

  /*
   * Remove address from address list
   * @param addressId    Id of the address entry in shopify
   * @param token        The customer access token
   */
  async deleteAddress(addressId, token) {
    const mutation = gql`
			mutation customerAddressDelete($id: ID!, $customerAccessToken: String!) {
				customerAddressDelete(id: $id, customerAccessToken: $customerAccessToken) {
					customerUserErrors {
						code
						field
						message
					}
					deletedCustomerAddressId
				}
			}
		`;

    const variables = {
      id: addressId,
      customerAccessToken: token
    };

    const data = await graphQLClient.request(mutation, variables);
    return data;
  },

  /*
   * Reset password for user
   * @param id           Reset-Id from the reset-email
   * @param token        The customer access token
   * @param password     New password
   */
  async customerReset(id, token, password) {
    const mutation = gql`
			mutation customerReset($id: ID!, $input: CustomerResetInput!) {
				customerReset(id: $id, input: $input) {
					customer {
						id
					}
					customerAccessToken {
						accessToken
						expiresAt
					}
					customerUserErrors {
						code
						field
						message
					}
				}
			}
		`;

    const variables = {
      id,
      input: {
        resetToken: token,
        password
      }
    };

    try {
      const data = await graphQLClient.request(mutation, variables);
      return data;
    } catch (err) {
      return null;
    }
  },

  async getMetaObjects(country: string, lang: string = 'en') {
    const query = gql`
			query getMetaobjects @inContext(language: ${lang.toUpperCase()}, country: ${country}){
				machine_types: metaobjects(type: "machine_types", first: 100) {
					edges {
						node {
							handle
							label: field(key: "label") {
								value
							}
							icon: field(key: "icon") {
								reference {
									... on MediaImage {
										image {
											url(transform: { maxHeight: 128, maxWidth: 128, preferredContentType: WEBP })
										}
									}
								}
							}
						}
					}
				}
				tasting_notes: metaobjects(type: "tasting_notes", first: 100) {
					edges {
						node {
							handle
							label: field(key: "label") {
								value
							}
              order: field(key: "order") {
								value
							}
							icon: field(key: "icon") {
								reference {
									... on MediaImage {
										image {
											url(transform: { maxHeight: 128, maxWidth: 128, preferredContentType: WEBP })
										}
									}
								}
							}
						}
					}
				}
				size_filters: metaobjects(type: "size_filters", first: 100) {
					edges {
						node {
							handle
							value: field(key: "value") {
								value
							}
						}
					}
				}
				tag_filters: metaobjects(type: "tag_filters", first: 100) {
					edges {
						node {
							handle
							value: field(key: "value") {
								value
							}
						}
					}
				}
			}
		`;

    try {
      const data = await graphQLClient.request(query);

      return data;
    } catch (err) {
      console.error(err);

      error(500, `Internal error: ${err}`);
    }
  },

  /**
   *
   */
  async prediciveSearch(search: string, lang: string = 'en', country: string) {
    const mutation = gql`
    query suggestions($query: String!) @inContext(language: ${lang.toUpperCase()}, country: ${country}) {
      predictiveSearch(query: $query) {
        products {
          id,
          title,
          handle,
          product_type: metafield(namespace: "custom", key: "product_type") {
            value
          },
          featuredImage {
            id
            altText
            url (transform: {
              maxHeight: 256,
              maxWidth: 256, 
              preferredContentType: WEBP
            })
          }
        }
      }
    }
  `;

    const variables = {
      query: search
    };

    try {
      const data = await graphQLClient.request(mutation, variables);
      return data;
    } catch (err) {
      return null;
    }
  }

  // Doesn't work, only available in the admin api...
  /*
  async getRessourceTranslations(resourceId: string, locales: string[]) {
    const query = gql`
      query {
        translatableResource(resourceId: "${resourceId}") {
          resourceId
          translations(locale: "en") {
            key
            value
          }
          translations(locale: "de") {
            key
            value
          }
        }
      }
    `;

    console.log(query)

    try {
      const data = await graphQLClient.request(query);
      return data;
    } catch (err) {
      return null;
    }
  }
  */
};
